import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import apiService from '@/services/api';

export const RemovedStudentsDialog = ({ open, onOpenChange, onRestored }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    apiService.listRemovedStudents().then(setRows).catch(() => toast.error('Could not load removed students')).finally(() => setLoading(false));
  }, [open]);

  const restore = async (s) => {
    setBusy(s.id);
    try {
      await apiService.restoreStudent(s.id);
      setRows((r) => r.filter((x) => x.id !== s.id));
      toast.success(`${s.name} restored`);
      onRestored?.();
    } catch (e) {
      toast.error(e.message || 'Failed to restore');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="removed-students-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trash2 className="h-5 w-5" /> Removed Students</DialogTitle>
          <DialogDescription>Students removed from your directory. Restore anyone removed by mistake.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="removed-empty">No removed students.</p>
        ) : (
          <ul className="divide-y max-h-[50vh] overflow-auto" data-testid="removed-list">
            {rows.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`removed-row-${s.id}`}>
                <div className="min-w-0">
                  <p className="font-medium truncate">{s.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {[s.department, s.student_number && `ID ${s.student_number}`, s.date_of_birth && `DoB ${s.date_of_birth}`].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="secondary">Removed</Badge>
                  <Button size="sm" variant="outline" onClick={() => restore(s)} disabled={busy === s.id} className="gap-1" data-testid={`restore-student-${s.id}`}>
                    {busy === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Restore
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};
